import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Breadcrumbs from "components/Common/Breadcrumb";
import DataTable from "../../components/Common/LocalizedDataTable";
import { OfficialHolidaysApi } from "apis/officialHolidays";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import config from "config/config";
import Swal from "../../helpers/swal_helper";
import moment from "moment";
import { formatNumber } from "../../helpers/language_helper";

function OfficialHolidaysList(props) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [activeData, setActiveData] = useState({
    page: 1,
    totalPage: 1,
    search: "",
    limit: config.LIMIT,
    status: null,
  });

  useEffect(() => {
    getAllOfficialHolidays(activeData);
  }, []);

  const columns = [
    {
      name: props.t("Sr.No"),
      selector: (row) => row.id,
      cell: (row) => formatNumber(row.id),
      sortable: true,
    },
    {
      name: props.t("Title"),
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: props.t("Start Date"),
      selector: (row) => moment(row.start_date).format("DD MMM Y"),
      sortable: true,
    },
    {
      name: props.t("End Date"),
      selector: (row) => moment(row.end_date).format("DD MMM Y"),
      sortable: true,
    },
    {
      name: props.t("Status"),
      selector: (row) => (row.status ? props.t("Active") : props.t("Inactive")),
      sortable: true,
      cell: (row) => (
        <span
          style={{
            color: row.status ? "#28a745" : "#dc3545",
            fontWeight: "500",
          }}
        >
          {row.status ? props.t("Active") : props.t("Inactive")}
        </span>
      ),
    },
    {
      name: props.t("Action"),
      selector: (row) => row.action,
    },
  ];

  const getAllOfficialHolidays = (data) => {
    setLoading(true);
    OfficialHolidaysApi.getOfficialHolidays(data)
      .then((res) => {
        if (res.data.status) {
          setActiveData({
            ...activeData,
            totalPage: res.data.pagination.total,
          });
          let tableData = [];
          res.data.data.forEach((v, i) => {
            tableData[i] = {
              id: i + 1,
              title: v.title,
              start_date: v.start_date,
              end_date: v.end_date,
              status: v.status,
              action: (
                <>
                  <Link
                    style={{ marginRight: "15px" }}
                    to={`/edit-official-holiday/${v.id}`}
                  >
                    <i className="fas fa-edit" id="edittooltip" />
                  </Link>
                  <i
                    className="mdi mdi-delete font-size-18"
                    style={{ color: "red" }}
                    id="deletetooltip"
                    onClick={() => onDelete(v.id)}
                  />
                </>
              ),
            };
          });
          setData(tableData);
        } else {
          Swal.fire({
            title: "Error",
            text: res.data.message,
            icon: "error",
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch official holidays",
          icon: "error",
        });
      });
  };

  const onDelete = (id) => {
    Swal.fire({
      title: props.t("Are you sure?"),
      text: props.t("You won't be able to revert this!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: props.t("Yes, delete it!"),
      cancelButtonText: props.t("Cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        OfficialHolidaysApi.deleteOfficialHoliday(id)
          .then((res) => {
            if (res.data.status) {
              Swal.fire("Deleted!", res.data.message, "success");
              getAllOfficialHolidays(activeData);
            } else {
              Swal.fire("Error!", res.data.message, "error");
            }
          })
          .catch((err) => {
            console.log(err);
            Swal.fire("Error!", "Failed to delete official holiday", "error");
          });
      }
    });
  };

  const handleChange = (page) => {
    const updatedData = { ...activeData, page };
    setActiveData(updatedData);
    getAllOfficialHolidays(updatedData);
  };

  const handleRowChange = (limit) => {
    const updatedData = { ...activeData, limit };
    setActiveData(updatedData);
    getAllOfficialHolidays(updatedData);
  };

  //meta title
  document.title = props.t("Official Holidays") + " | " + props.t("Salon");

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={props.t("Dashboard")}
          breadcrumbItem={props.t("Official Holidays")}
        />
        <Link
          to="/add-official-holiday"
          className="btn btn-primary w-xs btn-right"
        >
          {props.t("Add")}
        </Link>
      </div>

      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder={props.t("Search by title")}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                const updated = {
                  ...activeData,
                  search: e.target.value,
                  page: 1,
                };
                setActiveData(updated);
                getAllOfficialHolidays(updated);
              }}
            />
          </div>

          <div className="col-md-3">
            <select
              className="form-control"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                const updated = {
                  ...activeData,
                  status: e.target.value === "All" ? null : e.target.value === "true",
                  page: 1,
                };
                setActiveData(updated);
                getAllOfficialHolidays(updated);
              }}
            >
              <option value="All">{props.t("All")}</option>
              <option value="true">{props.t("Active")}</option>
              <option value="false">{props.t("Inactive")}</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={data}
                  pagination
                  paginationServer
                  paginationTotalRows={activeData.totalPage}
                  onChangeRowsPerPage={handleRowChange}
                  onChangePage={handleChange}
                  progressPending={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

OfficialHolidaysList.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OfficialHolidaysList); 